import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../../../components/shared/Button';
import { useLanguage } from '../../../hooks/shared';

interface ActionTooltipProps {
  label: string;
  disabled?: boolean;
  onConfirm: () => void | Promise<void>;
  variant?: 'default' | 'danger';
  showValidExcuseOption?: boolean;
  onConfirmWithExcuse?: (withExcuse: boolean) => void | Promise<void>;
  openDown?: boolean; // Open tooltip downward instead of upward
  children: React.ReactNode;
}

const ActionTooltip: React.FC<ActionTooltipProps> = ({
  label,
  disabled = false,
  onConfirm,
  variant = 'default',
  showValidExcuseOption = false,
  onConfirmWithExcuse,
  openDown = false,
  children,
}) => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [withValidExcuse, setWithValidExcuse] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleConfirm = async () => {
    try {
      if (showValidExcuseOption && onConfirmWithExcuse) {
        await onConfirmWithExcuse(withValidExcuse);
      } else {
        await onConfirm();
      }
    } catch (error) {
      console.error('Error in handleConfirm:', error);
    } finally {
      setIsOpen(false);
      setWithValidExcuse(false);
    }
  };

  if (disabled) {
    return <div className="inline-block">{children}</div>;
  }

  return (
    <div className="relative inline-block">
      <div
        ref={triggerRef}
        onClick={handleTriggerClick}
        className="cursor-pointer"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleTriggerClick(e as any);
          }
        }}
      >
        {children}
      </div>

      {isOpen && (
        <div
          ref={tooltipRef}
          className="absolute z-[9999] p-2 bg-white rounded-lg shadow-lg border border-gray-200"
          style={{
            left: '50%',
            transform: 'translateX(-50%)',
            ...(openDown
              ? {
                  top: '100%',
                  marginTop: '4px',
                }
              : {
                  bottom: '100%',
                  marginBottom: '4px',
                }),
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {showValidExcuseOption && (
            <label className="flex items-center gap-2 mb-2 text-xs text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                checked={withValidExcuse}
                onChange={(e) => setWithValidExcuse(e.target.checked)}
              />
              <span>{t('calendar.validExcuse')}</span>
            </label>
          )}

          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={handleConfirm}
              className={`${
                variant === 'danger'
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : ''
              }`}
            >
              {label}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActionTooltip;
